import type { ReactElement } from 'react';
import type React from 'react';
import {
  Td,
  Tr,
  Chip,
  TextLink,
  colors,
  Flex,
  RiSquareFill,
  Label,
  RiFilePaperLine,
  RiPencilLine
} from '@itandi/itandi-bb-ui';
import type { DealIndexVendor } from '~/models/Deal';
import { PATH as VENDOR_DEAL_PATH } from '~/pages/vendors/deals/[id]';
import { DealTypeChip } from '~/components/common/parts/chip/DealTypeChip';
import { processedHistory } from '~/models/DealHistory';
import { truncate } from '~/utils/StringUtil';
import { statusColor } from '~/models/Status';
import { DateCell } from '~/components/common/deals/index/DateCell';
import { DealInspectionDateCell } from '~/components/common/deals/index/DealInspectionDateCell';
import { StatusIcon } from '~/components/deprecated/StatusIcon';

type Props = {
  deal: DealIndexVendor;
  statusNames: { [p: string]: string };
};

export const DealTableRow: React.FC<Props> = ({ deal, statusNames }: Props) => {
  const historyRegistered = (
    historyType: 'estimations_created' | 'completion_report_created'
  ): ReactElement => (
    <>
      {processedHistory(deal, historyType) ? (
        <Label>登録済み</Label>
      ) : (
        <Flex>
          <Label>
            {historyType === 'estimations_created'
              ? '未登録'
              : `${deal.completionReportImageRequired ? '未登録' : '任意'}`}
          </Label>
          {(historyType === 'estimations_created' ||
            (historyType === 'completion_report_created' &&
              deal.completionReportImageRequired)) && (
            <StatusIcon variant="Alert" />
          )}
        </Flex>
      )}
    </>
  );
  const unread = deal.unreadMessageCount > 0 || deal.displayUnread;
  const unreadChipLabel =
    deal.unreadMessageCount > 0 ? `未読${deal.unreadMessageCount}` : '未読';
  return (
    <Tr>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex alignItems="flex-start" direction="column" gap={2}>
          <Chip
            Icon={RiSquareFill}
            iconColor={statusColor[deal.status]}
            label={statusNames[deal.status]}
          />
          {unread && (
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              label={unreadChipLabel}
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasTemporaryDealCancel && (
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="取下申請あり"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasRequestEstimation && (
            <Chip
              background={colors['expressive-blue']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請中"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasDraftEstimation && (
            <Chip
              background={colors['expressive-yellow-dark']}
              borderColor={null}
              Icon={RiPencilLine}
              iconColor={colors['itandi-white']}
              label="見積編集中"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasRequestEstimationByCompany && (
            <Chip
              background={colors['expressive-aqua-dark']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請あり"
              textColor={colors['itandi-white']}
            />
          )}
        </Flex>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex alignItems="flex-start" direction="column" gap={2}>
          <DealTypeChip
            dealSubtype={deal.dealSubtype}
            dealType={deal.dealType}
          />
          <TextLink
            color="link"
            href={VENDOR_DEAL_PATH(deal.id)}
            target="_blank"
            weight="bold"
          >
            {`${truncate(deal.name, 30)} ${deal.roomNumber || ''}`}
          </TextLink>
          <Label color="subtle">{truncate(deal.address, 35)}</Label>
          {deal.tenantName && (
            <Label color="subtle">
              {`${truncate(deal.tenantName, 15)} 様`}
            </Label>
          )}
        </Flex>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Label>{deal.maintenanceCompanyName}</Label>
        <br />
        <Label size="xs">{deal.maintenanceCompanyStoreName}</Label>
        <br />
        <Label color="subtle" size="xs">
          {truncate(deal.staff.name, 35)}
        </Label>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex direction="column" gap={2}>
          <Label>{deal.originVendorCompany.name}</Label>
          {deal.originVendorUserName ? (
            <Label color="subtle" size="xs">
              {truncate(deal.originVendorUserName, 35)}
            </Label>
          ) : (
            <Flex>
              <Label size="xs">担当者設定なし</Label>
              <StatusIcon variant="Alert" />
            </Flex>
          )}
        </Flex>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex direction="column" gap={2}>
          {deal.subVendorCompany === null ? (
            <Label color="subtle">実施会社未指定</Label>
          ) : (
            <Label color="subtle">{deal.subVendorCompany.name}</Label>
          )}
          {deal.subVendorUserName ? (
            <Label color="subtle" size="xs">
              {truncate(deal.subVendorUserName, 35)}
            </Label>
          ) : (
            <Label color="subtle" size="xs">
              担当者設定なし
            </Label>
          )}
        </Flex>
      </Td>
      <DateCell date={deal.updatedDate} displayTime />
      <DateCell date={deal.terminationDate} />
      <DealInspectionDateCell deal={deal} />
      <DateCell date={deal.surveyDate} />
      <DateCell date={deal.sendEstimateDate} />
      <DateCell date={deal.orderDate} />
      <DateCell date={deal.constructionStartDate} />
      <DateCell date={deal.constructionEndDate} />
      <DateCell date={deal.constructionReportedDate} />
      <DateCell date={deal.completedDate} />
      <DateCell date={deal.preferredDate} />
      <DateCell date={deal.createdAt} />
      <Td textAlign="Left" verticalAlign="Top">
        {historyRegistered('completion_report_created')}
      </Td>
    </Tr>
  );
};
